import { DynamicPage } from '@betterfunnel/types';
import HorizontalLoader from '@betterfunnel/ui-core/HorizontalLoader';
import Modal, { ModalActionData } from '@betterfunnel/ui-core/Modal';
import TextArea from '@betterfunnel/ui-core/TextArea';
import TextField from '@betterfunnel/ui-core/TextField';
import { diffDynamicPageVersions } from '@betterfunnel/utils';
import { DialogTitle } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { diff } from 'deep-diff';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuditLog } from '../../contexts/AuditLogContext';
import { useDashboardContext } from '../../contexts/DashboardContext';
import { useSnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import {
  createDynamicPageVersion,
  createPreviewDynamicPageVersion,
  getPrevDynamicPageVersion,
} from '../../services/DynamicPagesService';

interface Props {
  dynamicPages: QueryDocumentSnapshot<DynamicPage>[] | string[];
  open: boolean;
  onClose: () => void;
}

export default function BulkPublishDynamicPagesModal({
  dynamicPages: _dynamicPagesToEdit,
  open,
  onClose,
}: Props) {
  const { show } = useSnackbarContext();
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages } = useDashboardContext();
  const auditLog = useAuditLog();

  const [versionName, setVersionName] = useState<string>('');
  const [versionDescription, setVersionDescription] = useState<string>('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | string[]>();

  const [publishProgress, setPublishProgress] = useState(0);

  useEffect(() => {
    if (!open) return;

    setVersionName('');
    setVersionDescription('');
    setPublishProgress(0);
    setSaving(false);
    setError(undefined);
  }, [open]);

  const bulkAutoPublish = useCallback(async () => {
    if (saving) return;
    if (!selectedAccount) return;
    if (!dynamicPages) return;

    if (versionName.length < 2) {
      setError('Version name is required');
      return;
    }

    setSaving(true);

    try {
      const dynamicPagesToEdit = _dynamicPagesToEdit
        .map((d) =>
          typeof d === 'string' ? dynamicPages.find((p) => p.id === d) : d,
        )
        .filter(Boolean) as QueryDocumentSnapshot<DynamicPage>[];

      // dynamicPagesToEdit = dynamicPagesToEdit.filter((d) => {
      //   return d.data().hasChangesToPublish;
      // });

      const dynamicPagesIds = dynamicPagesToEdit.map((d) => d.id);
      auditLog.log(
        'update',
        'Bulk Auto Publish Dynamic Pages',
        'dynamicPages',
        undefined,
        undefined,
        diff({}, { versionName, versionDescription, dynamicPagesIds }),
      );

      if (!selectedAccount || !dynamicPages) return;

      const errors: string[] = [];
      for (const [index, dynamicPage] of dynamicPagesToEdit.entries()) {
        try {
          const newVersion = await createPreviewDynamicPageVersion(
            selectedAccount.id,
            dynamicPage.id,
          );
          const prevVersion = await getPrevDynamicPageVersion(
            selectedAccount.id,
            dynamicPage.id,
          );
          const versionsDiff = prevVersion[0]
            ? diffDynamicPageVersions(prevVersion[0].data(), newVersion)
            : [];
          await createDynamicPageVersion(selectedAccount.id, dynamicPage.id, {
            ...newVersion,
            name: 'Bulk - ' + versionName,
            description: versionDescription,
            changes: JSON.parse(JSON.stringify(versionsDiff)),
          });

          setPublishProgress((index / dynamicPagesToEdit.length) * 100);
        } catch (e: any) {
          console.error(e);
          errors.push(
            `Failed to publish, ${dynamicPage.data().name} with error: ${e.message}`,
          );
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 300));

      if (errors.length > 0) {
        setError(errors);
        setSaving(false);
        return;
      }

      onClose();

      show('Bulk publish completed successfully', 'success');
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e.message);
    }
    setSaving(false);
  }, [
    _dynamicPagesToEdit,
    auditLog,
    dynamicPages,
    onClose,
    saving,
    selectedAccount,
    show,
    versionDescription,
    versionName,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: 'Bulk Publish',
        type: 'primary',
        loading: saving,
        onClick: () => bulkAutoPublish(),
      },
    ],
    [saving, onClose, bulkAutoPublish],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions}>
      <DialogTitle
        as="h3"
        className="text-base leading-6 font-semibold text-gray-900"
      >
        Bulk Publish Dynamic Pages
      </DialogTitle>
      <div className="relative mt-4 flex min-h-48 w-full flex-col gap-4">
        <TextField
          title="Version Name"
          value={versionName}
          onChange={(e) => setVersionName(e.target.value)}
          placeholder="Version Name"
          readOnly={saving}
        />

        <TextArea
          title="Version Description"
          value={versionDescription}
          onChange={(e) => setVersionDescription(e.target.value)}
          placeholder="Version Description"
          rows={4}
          readOnly={saving}
        />

        {saving && <HorizontalLoader progress={publishProgress} />}
      </div>
      {error?.length ? (
        <div className="mt-5 w-full text-start text-red-600">
          {(Array.isArray(error) ? error : [error]).map((err, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={i} className="text-sm">
              {err || 'An error occurred'}
            </p>
          ))}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
